<template>
  <transition name="brand-panel-dropdown">
    <div v-if="show" class="brand-panel" @click.stop>
      <div class="panel-header">
        <h4 class="panel-title">{{ label }}</h4>
        <div class="selected-info" v-if="selectedItem">已选: {{ selectedItem.name }}</div>
      </div>

      <div class="panel-content">
        <div class="search-box" v-if="brandList.length > 8">
          <el-input
            v-model="searchKeyword"
            :placeholder="searchPlaceholder"
            size="small"
            prefix-icon="el-icon-search"
            clearable
            @input="handleSearch"
          ></el-input>
        </div>

        <div class="brand-list-container">
          <div v-if="filteredBrandList.length === 0" class="empty-state">
            <i class="el-icon-search"></i>
            <p>未找到相关选项</p>
          </div>

          <div class="brand-grid" v-else>
            <div
              :class="{
                'brand-item': true,
                active: isSelected(item)
              }"
              v-for="item in filteredBrandList"
              :key="String(item.value) + '-' + item.name"
              @click="chooseBrand(item)"
            >
              <span class="brand-name">{{ item.name }}</span>
              <div class="check-icon" v-if="isSelected(item)">
                <i class="el-icon-check"></i>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="panel-footer">
        <div class="selected-tags" v-if="selectedItem">
          <div class="selected-tags-container">
            <span class="selected-tag" @click="removeSelected">
              {{ selectedItem.name }}
              <i class="el-icon-close"></i>
            </span>
          </div>
        </div>

        <div class="action-buttons">
          <el-button size="small" class="btn-cancel" @click.stop="cancel">
            取消
          </el-button>
          <el-button size="small" class="btn-reset" @click.stop="reset" :disabled="!selectedItem">
            重置
          </el-button>
          <el-button type="primary" size="small" class="btn-confirm" @click.stop="confirm">
            确定
          </el-button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: "BrandPanel",
  props: {
    label: {
      type: String,
      default: "筛选"
    },
    searchPlaceholder: {
      type: String,
      default: "搜索..."
    }
  },
  data() {
    return {
      show: false,
      brandList: [],
      selectedItem: null,
      searchKeyword: "",
      filteredBrandList: [],
      /** true：购物车 string[]，确认抛 value 数组；false：配件列表对象，确认抛单项 */
      emitValueList: false
    };
  },
  methods: {
    isSelected(item) {
      return !!this.selectedItem && String(this.selectedItem.value) === String(item.value);
    },

    reset() {
      this.selectedItem = null;
      this.searchKeyword = "";
      this.filteredBrandList = [...this.brandList];
    },

    /**
     * 选项归一化：购物车字典为 string[]，配件列表筛选为 {name,value,id}[]。
     * 对象须保留原字段（如 id），配件列表确认后要读 selectedItem.id。
     */
    normalizeOptions(list) {
      if (!Array.isArray(list)) return [];
      return list
        .map(item => {
          if (item == null) return null;
          if (typeof item === "string" || typeof item === "number") {
            return { name: String(item), value: item };
          }
          if (!item.name) return null;
          const value = item.value != null ? item.value : item.name;
          if (String(value) === "") return null;
          return { ...item, value };
        })
        .filter(Boolean);
    },

    resolveSelected(selected) {
      if (selected == null || selected === "") return null;
      const first = Array.isArray(selected) ? selected[0] : selected;
      if (first == null || first === "") return null;
      const key = typeof first === "object" ? first.value || first.name || first.id : first;
      return (
        this.brandList.find(
          e => String(e.value) === String(key) || e.name === key || (e.id && String(e.id) === String(key))
        ) || null
      );
    },

    // selected：配件列表为 value/name/id 字符串；购物车为已选名称数组
    open(list, selected) {
      this.show = true;
      this.searchKeyword = "";
      this.emitValueList =
        Array.isArray(selected) ||
        (Array.isArray(list) && list.some(item => typeof item === "string" || typeof item === "number"));
      this.brandList = this.normalizeOptions(list);
      this.selectedItem = this.resolveSelected(selected);
      this.filteredBrandList = [...this.brandList];

      // 点击外部关闭
      setTimeout(() => {
        document.addEventListener("click", this.handleClickOutside);
      }, 0);
    },

    handleSearch() {
      if (!this.searchKeyword) {
        this.filteredBrandList = [...this.brandList];
        return;
      }

      const keyword = this.searchKeyword.toLowerCase();
      this.filteredBrandList = this.brandList.filter(item => item.name.toLowerCase().includes(keyword));
    },

    // 单选：点击已选中项取消，点击其他项替换
    chooseBrand(item) {
      if (this.isSelected(item)) {
        this.selectedItem = null;
      } else {
        this.selectedItem = { ...item };
      }
    },

    removeSelected() {
      this.selectedItem = null;
    },

    handleClickOutside(event) {
      if (!this.$el?.contains(event.target)) {
        this.cancel();
      }
    },

    cancel() {
      document.removeEventListener("click", this.handleClickOutside);
      this.show = false;
      this.searchKeyword = "";
      setTimeout(() => {
        this.filteredBrandList = [];
      }, 300);
      this.$emit("close");
    },

    confirm() {
      if (this.emitValueList) {
        this.$emit("change", this.selectedItem ? [this.selectedItem.value] : []);
      } else {
        this.$emit("change", this.selectedItem ? { ...this.selectedItem } : null);
      }
      this.cancel();
      document.removeEventListener("click", this.handleClickOutside);
    }
  }
};
</script>

<style lang="scss" scoped>
.brand-panel {
  position: absolute;
  top: calc(100% - 12px);
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #e4e7ed;
  border-top: none;
  border-radius: 0 0 8px 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);
  z-index: 1000;
  max-height: 400px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.panel-header {
  padding: 12px 16px;
  border-bottom: 1px solid #f0f0f0;
  background: #fafafa;
  display: flex;
  align-items: center;
  justify-content: space-between;

  .panel-title {
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    color: #333;
  }

  .selected-info {
    font-size: 12px;
    color: var(--color-primary);
    background: #ecf5ff;
    padding: 2px 8px;
    border-radius: 10px;
  }
}

.panel-content {
  flex: 1;
  padding: 12px 16px;
  overflow-y: auto;
  max-height: 280px;

  .search-box {
    margin-bottom: 12px;

    ::v-deep .el-input__inner {
      border-radius: 6px;
      font-size: 12px;
    }
  }

  .empty-state {
    text-align: center;
    padding: 20px;
    color: #909399;

    i {
      font-size: 24px;
      margin-bottom: 8px;
      opacity: 0.5;
    }

    p {
      margin: 0;
      font-size: 12px;
    }
  }
}

.brand-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 8px;
}

.brand-item {
  position: relative;
  background: #f8f9fa;
  border: 1px solid #e4e7ed;
  border-radius: 6px;
  padding: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 32px;

  &:hover {
    border-color: var(--color-primary);
    background: #f0f7ff;
    transform: translateY(-1px);
  }

  &.active {
    background: linear-gradient(135deg, #ecf5ff 0%, #d9ecff 100%);
    border-color: var(--color-primary);

    .brand-name {
      color: var(--color-primary);
      font-weight: 600;
    }
  }
  .brand-name {
    font-size: 12px;
    color: #333;
    font-weight: 500;
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .check-icon {
    color: var(--color-primary);
    font-size: 12px;
    font-weight: bold;
    margin-left: 4px;
    flex-shrink: 0;
  }
}

.panel-footer {
  padding: 12px 16px;
  border-top: 1px solid #f0f0f0;
  background: #fafafa;

  .selected-tags {
    margin-bottom: 12px;

    .selected-tags-container {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
      max-height: 60px;
      overflow-y: auto;
    }

    .selected-tag {
      background: linear-gradient(135deg, var(--color-primary) 0%, #66b1ff 100%);
      color: white;
      padding: 2px 8px;
      border-radius: 12px;
      font-size: 11px;
      display: flex;
      align-items: center;
      gap: 4px;
      cursor: pointer;
      transition: all 0.2s;
      line-height: 1.2;

      &:hover {
        background: linear-gradient(135deg, #f56c6c 0%, #f78989 100%);
        transform: scale(1.05);
      }

      i {
        font-size: 10px;
        margin-left: 2px;
      }
    }
  }

  .action-buttons {
    display: flex;
    justify-content: flex-end;
    gap: 8px;

    .btn-cancel {
      border-color: #dcdfe6;
      font-size: 12px;
      padding: 6px 12px;

      &:hover {
        border-color: #c0c4cc;
        background-color: #f5f7fa;
      }
    }

    .btn-reset {
      color: #f56c6c;
      border-color: #f56c6c;
      font-size: 12px;
      padding: 6px 12px;

      &:hover {
        background-color: #fef0f0;
        border-color: #f78989;
      }

      &:disabled {
        color: #c0c4cc;
        border-color: #e4e7ed;
        background-color: #f5f7fa;
      }
    }

    .btn-confirm {
      background: linear-gradient(135deg, var(--color-primary) 0%, #66b1ff 100%);
      border: none;
      font-size: 12px;
      padding: 6px 16px;

      &:hover {
        background: linear-gradient(135deg, #66b1ff 0%, var(--color-primary) 100%);
        transform: translateY(-1px);
        box-shadow: 0 2px 6px rgba(64, 158, 255, 0.3);
      }

      &:disabled {
        background: #c0c4cc;
        transform: none;
        box-shadow: none;
      }
    }
  }
}

// 下拉动画
.brand-panel-dropdown-enter-active {
  animation: brand-panel-dropdown-in 0.2s ease-out;
}

.brand-panel-dropdown-leave-active {
  animation: brand-panel-dropdown-out 0.15s ease-in;
}

@keyframes brand-panel-dropdown-in {
  0% {
    opacity: 0;
    transform: translateY(-10px) scale(0.95);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes brand-panel-dropdown-out {
  0% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
  100% {
    opacity: 0;
    transform: translateY(-10px) scale(0.95);
  }
}

// 滚动条样式
.brand-panel ::-webkit-scrollbar {
  width: 4px;
}

.brand-panel ::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 2px;
}

.brand-panel ::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 2px;
}

.brand-panel ::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

// 响应式设计
@media (max-width: 768px) {
  .brand-panel {
    max-height: 350px;
  }

  .brand-grid {
    grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
    gap: 6px;
  }

  .panel-header,
  .panel-content,
  .panel-footer {
    padding: 10px 12px;
  }

  .action-buttons {
    flex-wrap: wrap;

    .el-button {
      flex: 1;
      min-width: 60px;
    }
  }
}
</style>
